import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Eye, EyeOff, Lock, UserCheck } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ActivatePartnerCollaborator = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [collaboratorInfo, setCollaboratorInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [isActivated, setIsActivated] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/validate-partner-collaborator-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email })
        });

        if (response.ok) {
          const data = await response.json();
          setCollaboratorInfo(data);
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } catch (err) {
        console.error('Error validating token:', err);
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const validatePassword = (pass) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumbers = /\d/.test(pass);
    return pass.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers;
  };

  const handleActivate = async () => {
    setError('');

    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (!validatePassword(password)) {
      setError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre');
      return;
    }

    setIsActivating(true);

    try {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      const response = await fetch(`${API_BASE_URL}/api/auth/activate-partner-collaborator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password })
      });

      if (response.ok) {
        setIsActivated(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de l\'activation');
      }
    } catch (err) {
      console.error('Error activating:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setIsActivating(false);
    }
  };

  // Écran de chargement
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-slate-600 mt-4">Vérification de votre invitation...</p>
        </div>
      </div>
    );
  }

  // Lien invalide ou expiré
  if (!isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="text-red-600" size={32} />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien invalide ou expiré</h1>
          <p className="text-slate-600 mb-6">
            Ce lien d'invitation n'est plus valide. Veuillez contacter votre courtier pour obtenir un nouveau lien.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  // Activation réussie
  if (isActivated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-emerald-600" size={32} />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Compte activé !</h1>
          <p className="text-slate-600 mb-6">
            Votre accès collaborateur a été activé avec succès. Vous pouvez maintenant vous connecter.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  // Formulaire de création de mot de passe
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCheck className="text-orange-600" size={32} />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Bienvenue {collaboratorInfo?.first_name} !</h1>
          <p className="text-slate-600 mt-2">
            Vous avez été invité(e) à rejoindre l'espace courtier en tant que collaborateur.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <Lock size={16} className="inline mr-1" />
              Créez votre mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirmez le mot de passe
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                placeholder="Confirmez votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
            Le mot de passe doit contenir :
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li className={password.length >= 8 ? 'text-emerald-600' : ''}>Au moins 8 caractères</li>
              <li className={/[A-Z]/.test(password) ? 'text-emerald-600' : ''}>Une lettre majuscule</li>
              <li className={/[a-z]/.test(password) ? 'text-emerald-600' : ''}>Une lettre minuscule</li>
              <li className={/\d/.test(password) ? 'text-emerald-600' : ''}>Un chiffre</li>
            </ul>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleActivate}
            disabled={isActivating}
            className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
          >
            {isActivating ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "Activer mon compte"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivatePartnerCollaborator;
